import { Link } from 'react-router-dom';
import { ArrowRight, CalendarHeart, Camera, CheckCircle2, Gift, HeartHandshake, Palette, Sparkles, Users } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const recipientIdeas = [
  {
    icon: Users,
    title: 'Pour un parent ou des grands-parents',
    photo: 'Photo de famille, moment avec les petits-enfants, souvenir de vacances ou portrait multi-générationnel.',
    style: 'Aquarelle pour une ambiance douce, peinture à l’huile pour un rendu plus premium.',
    source: 'family_recipient',
  },
  {
    icon: HeartHandshake,
    title: 'Pour un conjoint ou un couple',
    photo: 'Photo de couple, lieu symbolique, mariage civil, voyage ou moment partagé qui raconte votre histoire.',
    style: 'Huile ou impressionniste pour un tableau élégant, croquis pour un cadeau plus sobre.',
    source: 'couple_recipient',
  },
  {
    icon: Gift,
    title: 'Pour un ami proche',
    photo: 'Souvenir drôle, portrait de groupe, animal de compagnie ou moment complice facile à reconnaître.',
    style: 'Pop art pour l’énergie, croquis pour un rendu minimaliste, aquarelle pour rester polyvalent.',
    source: 'friend_recipient',
  },
];

const birthdayChecklist = [
  'Choisissez une photo immédiatement reconnaissable par la personne qui reçoit le cadeau.',
  'Gardez un sujet principal lisible : visage, couple, famille, animal ou lieu souvenir.',
  'Évitez les photos sombres, floues ou trop chargées si le tableau doit être affiché au mur.',
  'Ajoutez une note personnelle au moment de la commande plutôt qu’un message trop long dans l’image.',
];

const styleSuggestions = [
  {
    to: '/styles/oil-painting',
    title: 'Peinture à l’huile',
    helper: 'Pour un anniversaire important, un cadeau premium ou un portrait de famille à exposer.',
  },
  {
    to: '/styles/watercolor',
    title: 'Aquarelle',
    helper: 'Pour un rendu doux, lumineux et facile à offrir à un parent, conjoint ou grand-parent.',
  },
  {
    to: '/styles/pop-art',
    title: 'Pop art',
    helper: 'Pour un cadeau plus moderne, coloré et amusant à partir d’une photo expressive.',
  },
  {
    to: '/styles/sketch',
    title: 'Croquis',
    helper: 'Pour une intention sobre, élégante et décorative quand la photo est déjà très forte.',
  },
];

const planningSteps = [
  {
    title: '1. Partir de la personne',
    text: 'Le meilleur cadeau anniversaire personnalisé commence par le lien : parent, conjoint, ami, grand-parent ou amoureux des animaux.',
  },
  {
    title: '2. Choisir la photo qui raconte l’histoire',
    text: 'Une image imparfaite mais émotionnelle peut être plus forte qu’une photo techniquement froide, tant que le sujet reste lisible.',
  },
  {
    title: '3. Sélectionner un style adapté',
    text: 'Doux, premium, graphique ou minimaliste : le style doit renforcer l’intention du cadeau, pas l’écraser.',
  },
];

const relatedPages = [
  {
    to: '/idees-cadeaux-photo-personnalises',
    title: 'Idées cadeaux photo personnalisés',
    helper: 'Comparer les occasions et trouver d’autres angles cadeaux à partir d’une photo.',
  },
  {
    to: '/tableau-photo-personnalise',
    title: 'Tableau photo personnalisé',
    helper: 'Comprendre le parcours complet de la photo au tableau imprimé.',
  },
  {
    to: '/portrait-animal-personnalise',
    title: 'Portrait animal personnalisé',
    helper: 'Une piste très forte si la personne fête son anniversaire avec son chien, chat ou compagnon.',
  },
];

const createPath = (source: string) => `/create?source=birthday_gift_${source}`;

const trackBirthdayCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'cadeau-anniversaire-photo-personnalise',
    source: `birthday_gift_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/cadeau-anniversaire-photo-personnalise'),
      name: 'Cadeau anniversaire photo personnalisé',
      description:
        'Guide pour créer un cadeau anniversaire photo personnalisé : choix de la photo, style artistique, destinataire et création d’un tableau personnalisé.',
      url: absoluteUrl('/cadeau-anniversaire-photo-personnalise'),
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl(),
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: absoluteUrl(),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cadeau anniversaire photo personnalisé',
          item: absoluteUrl('/cadeau-anniversaire-photo-personnalise'),
        },
      ],
    },
  ],
};

const CadeauAnniversairePhotoPersonnalise = () => {
  return (
    <>
      <SEOHead
        title="Cadeau anniversaire photo personnalisé — Tableau unique | Ma Photo en Tableau"
        description="Créez un cadeau anniversaire photo personnalisé à partir d’un souvenir : conseils photo, styles artistiques, idées par destinataire et création de tableau personnalisé."
        canonical={absoluteUrl('/cadeau-anniversaire-photo-personnalise')}
        schema={schema}
      />
      <div className="min-h-screen overflow-x-hidden">
        <main>
          <section className="section-shell gradient-hero relative overflow-hidden pt-32 pb-20">
            <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
            <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
            <div className="container relative mx-auto px-6">
              <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                    Cadeau anniversaire photo personnalisé
                  </p>
                  <h1 className="display-title mb-6 text-foreground">
                    Offrez un <span className="text-premium">cadeau d’anniversaire unique</span> à partir d’une photo qui compte.
                  </h1>
                  <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                    Transformez un souvenir de famille, de couple, d’amitié ou d’animal en tableau personnalisé. Le parcours aide à choisir une photo lisible, un style artistique et un rendu prêt à devenir un vrai cadeau.
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                      <Link to={createPath('hero_primary')} onClick={() => trackBirthdayCta('hero_primary')}>
                        Créer le cadeau anniversaire
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                      <Link to="/idees-cadeaux-photo-personnalises">Voir d’autres idées cadeaux</Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                  <img
                    src="/site-assets/gift-unwrapping.jpg"
                    alt="Tableau familial déballé comme cadeau d’anniversaire personnalisé"
                    width={800}
                    height={533}
                    className="mb-6 aspect-[3/2] w-full rounded-[1.5rem] object-cover"
                  />
                  <CalendarHeart className="mb-5 h-10 w-10 text-primary" />
                  <h2 className="font-display text-4xl leading-none text-foreground">La bonne idée : une photo que la personne reconnaît instantanément.</h2>
                  <div className="mt-6 space-y-4">
                    {birthdayChecklist.map((tip) => (
                      <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Choix rapide</p>
                <h2 className="section-title text-foreground">Quel cadeau photo selon le destinataire ?</h2>
                <p className="mt-4 text-lg leading-8 text-foreground/65">
                  Une intention d’anniversaire se convertit mieux quand le visiteur retrouve vite son cas : famille, couple, ami proche ou animal de compagnie.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {recipientIdeas.map((idea) => (
                  <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                      <idea.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                    <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                    <Button variant="premium" className="mt-6 rounded-full" asChild>
                      <Link to={createPath(idea.source)} onClick={() => trackBirthdayCta(idea.source)}>
                        Créer pour ce profil
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-8 shadow-card backdrop-blur-md">
                  <Sparkles className="mb-5 h-8 w-8 text-primary" />
                  <h2 className="section-title text-foreground">Un cadeau personnel sans tomber dans le gadget.</h2>
                  <p className="mt-5 text-sm leading-7 text-foreground/68">
                    Un tableau photo personnalisé reste visible dans une maison, contrairement à beaucoup de cadeaux d’anniversaire vite rangés. L’objectif est de transformer un souvenir en objet décoratif simple à comprendre et agréable à offrir.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {planningSteps.map((step) => (
                    <div key={step.title} className="rounded-[1.5rem] border border-white/65 bg-white/60 p-5 shadow-card">
                      <h3 className="font-display text-2xl leading-none text-foreground">{step.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-foreground/62">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-3">Styles conseillés</p>
                  <h2 className="section-title text-foreground">Quel style artistique pour un anniversaire ?</h2>
                </div>
                <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                  <Link to={createPath('style_section')} onClick={() => trackBirthdayCta('style_section')}>Tester avec ma photo</Link>
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {styleSuggestions.map((style) => (
                  <Link key={style.to} to={style.to} className="group rounded-[1.5rem] border border-white/65 bg-white/65 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <Palette className="mb-4 h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground group-hover:text-primary">{style.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/60">{style.helper}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Voir le style
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Guides liés</p>
                <h2 className="section-title text-foreground">Continuer à comparer avant de créer.</h2>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {relatedPages.map((page) => (
                  <Link key={page.to} to={page.to} className="group rounded-[1.6rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <h3 className="font-semibold text-foreground group-hover:text-primary">{page.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/60">{page.helper}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Lire le guide
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell pb-24 pt-10">
            <div className="container mx-auto px-6 text-center">
              <div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10">
                <Camera className="mx-auto mb-5 h-8 w-8 text-primary" />
                <h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à créer un cadeau anniversaire photo ?</h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">
                  Importez une photo, choisissez un style artistique, puis avancez vers le support qui correspond au cadeau.
                </p>
                <Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild>
                  <Link to={createPath('final_cta')} onClick={() => trackBirthdayCta('final_cta')}>
                    Commencer mon cadeau anniversaire
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CadeauAnniversairePhotoPersonnalise;
